/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import AuthService from 'cio/services/auth';
import { action } from '@ember/object';
import RelationshipModel from 'cio/models/relationship';
import DS from 'ember-data';
import ObjectModel from 'cio/models/object';
import ObjectTypeModel from 'cio/models/object-type';
import AttributesProxy from 'cio/app-models/customer/attributes-proxy';
import AlertsService from 'cio/services/alerts';
import CustomerModel from 'cio/models/customer';
import { dropTask } from 'ember-concurrency';
import SnackbarService from 'cio/services/snackbar';

interface Args {
  showObjectView: boolean;
  onClose: () => unknown;
  relationship: RelationshipModel;
  attributesProxy: AttributesProxy;
  editingAttributesForm: boolean;
  setEditMode: (value: boolean) => unknown;
  setAttributesProxy: (r: RelationshipModel) => unknown;
}

export default class RelationshipsTableComponent extends Component<Args> {
  @service auth!: AuthService;
  @service store!: DS.Store;
  @service alerts!: AlertsService;
  @service snackbar!: SnackbarService;

  @tracked editAttributes = false;
  @tracked searchTerm = '';

  get customer() {
    return this.args.relationship.belongsTo('customer').value() as CustomerModel;
  }

  get object() {
    return this.args.relationship.belongsTo('object').value() as ObjectModel;
  }

  get objectType() {
    return this.object.belongsTo('objectType').value() as ObjectTypeModel;
  }

  get matchedAttrs() {
    let { attrs } = this.args.relationship;
    let trimmedSearchTerm = this.searchTerm.trim();

    if (trimmedSearchTerm === '') {
      return attrs;
    }

    return attrs.filter((attr: Record<string, string>) => {
      let [key] = Object.keys(attr);

      return key.includes(trimmedSearchTerm);
    });
  }

  @action
  removeAttribute(attribute: Record<string, any>) {
    this.args.attributesProxy.remove(attribute as any);
  }

  @action
  hideAttribute(attribute: Record<string, any>) {
    this.args.attributesProxy.hideAttribute(attribute);
  }

  save = dropTask(async () => {
    let { attrs, hiddenAttrs } = this.args.attributesProxy.getModelAttributes();
    try {
      let updatedRelationship = await this.args.relationship.updateRelationship(attrs, hiddenAttrs);
      this.snackbar.add('relationship.attribute.success', 'Relationship updated');
      this.args.setEditMode(Object.keys(attrs).length === 0);
      this.args.setAttributesProxy(updatedRelationship as RelationshipModel);
    } catch {
      this.alerts.error(
        'relationship.attribute.error',
        'There was an error updating the relationship, please try again later',
      );
    }
  });

  @action
  cancel() {
    this.args.attributesProxy.reset();
    this.args.relationship.rollbackAttributes();

    if (this.args.relationship.attrs.length === 0) {
      this.args.onClose();
    } else {
      this.args.setEditMode(false);
    }
  }
}
